import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Setting keys (read server-side by the viewer's set-price API)
const CUSTOM_QUOTE_SETTINGS = {
  VEHICLE_CHANGE_ENABLED: 'operator_quote_vehicle_change_enabled',
} as const

const customQuoteSchema = z.object({
  vehicle_change_enabled: z.boolean().default(true),
})

type CustomQuoteFormData = z.infer<typeof customQuoteSchema>

/**
 * Settings for the operator-priced Custom Quotes (Get Quote) flow. The master
 * switch that turns the flow on lives on the Booking tab; this tab holds the
 * behavior of the set-price page itself.
 */
export function CustomQuoteSettingsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Same query key as BookingPage so both share one cache entry
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  const form = useForm<CustomQuoteFormData>({
    resolver: zodResolver(customQuoteSchema),
    defaultValues: {
      vehicle_change_enabled: true,
    },
  })

  useEffect(() => {
    if (settings) {
      form.reset({
        // Default true: a missing row means vehicle change is allowed
        vehicle_change_enabled:
          settings[CUSTOM_QUOTE_SETTINGS.VEHICLE_CHANGE_ENABLED] !== 'false',
      })
    }
  }, [settings, form])

  const mutation = useMutation({
    mutationFn: async (data: CustomQuoteFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const { error } = await supabase.from('account_settings').upsert(
        {
          account_id: activeAccount.id,
          key: CUSTOM_QUOTE_SETTINGS.VEHICLE_CHANGE_ENABLED,
          value: String(data.vehicle_change_enabled),
          description:
            'Allow changing the vehicle on the operator set-price page (Custom Quotes)',
          is_secret: false,
        },
        { onConflict: 'account_id,key' }
      )

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Custom quote settings saved',
        description: 'Your custom quote settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Quote Settings</CardTitle>
        <CardDescription>
          Configure the set-price page you use to price Get Quote requests. The
          Custom Quotes master switch is on the Booking tab.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="vehicle_change_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Allow vehicle change when setting a price
                    </FormLabel>
                    <FormDescription>
                      Show a vehicle dropdown on the set-price page so you can
                      swap the vehicle the customer requested — for example when
                      the requested vehicle is unavailable for that date. The
                      quote (and the resulting booking) is updated to the
                      vehicle you pick.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
